import { optimized } from "./img";
import { useEffect } from "react";
import { fitTitle, shortenDesc, normaliseGraph } from "../content/seoShared";
import { useLocation } from "react-router-dom";
import { seoFor, isPrivatePath, isNoindexFollow, isProductionHost } from "./seoRoutes";
import { HOME_FAQS } from "../mock";
import { crumbsFor, crumbsLd } from "../content/crumbs";
import { PAGE_FAQS } from "../content/pageFaqs";
import { destinationNames } from "../components/Breadcrumbs";

export const SITE_URL = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://vistolane.com");
export const SITE_NAME = "Vistolane";
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const meta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content || "");
};
const link = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

let overrides = null;
let loading = null;
export const loadSeoOverrides = () => {
  if (overrides) return Promise.resolve(overrides);
  if (!loading) loading = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/overrides`).then((r) => (r.ok ? r.json() : {})).then((d) => { overrides = d || {}; return overrides; }).catch(() => { overrides = {}; return overrides; });
  return loading;
};

export function applySeo(props) {
  const p = props.path || window.location.pathname;
  const o = overrides ? overrides[p] : null;
  if (!overrides) loadSeoOverrides().then((all) => { if (all[p] && (props.path || window.location.pathname) === p) applySeo(props); });
  _apply(o ? { ...props, title: o.title || props.title, description: o.description || props.description, image: o.og_image || props.image, noindex: o.noindex ?? props.noindex, canonical: o.canonical, schemaType: o.schema_type } : props);
}

// alternates: [[hreflang, path]] — rendered as <link rel="alternate" hreflang> and reset on every route so stale pairs never leak.
function applyAlternates(alternates) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  for (const [lang, href] of alternates || []) { const el = document.createElement("link"); el.setAttribute("rel", "alternate"); el.setAttribute("hreflang", lang); el.setAttribute("href", href.startsWith("http") ? href : `${SITE_URL}${href}`); document.head.appendChild(el); }
  document.documentElement.lang = (alternates || []).find(([l, h]) => h === window.location.pathname)?.[0]?.split("-")[0] || "en";
}

// Initial load: when the HTML we were served was prerendered for this very URL (<meta name="vl:path">), it already carries the title, description,
// canonical, Open Graph, hreflang and JSON-LD from the same record the app would compute, so the client leaves the head alone.
// The head is only rewritten after an in-app router navigation (or when the host handed us a fallback document for another path).
const norm = (p) => (String(p || "/").replace(/\/index\.html$/, "").replace(/\/+$/, "").toLowerCase() || "/");
let navigated = false;
const initialPath = typeof window !== "undefined" ? norm(window.location.pathname) : "/";
const servedForPath = (p) => { const m = document.querySelector('meta[name="vl:path"]'); return Boolean(m && m.content === norm(p)); };
export const noteNavigation = (pathname) => { if (norm(pathname) !== initialPath) navigated = true; };

function _apply({ title, description: rawDescription, path, image, type = "website", noindex = false, jsonLd, canonical, schemaType, alternates, status }) {
  const p = path || window.location.pathname;
  if (!navigated && norm(p) === initialPath && servedForPath(p) && !status) {
    document.documentElement.setAttribute("data-seo", "live");
    return;
  }
  applyAlternates(alternates);
  const offProduction = typeof window !== "undefined" && !isProductionHost(window.location.hostname) && !/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  const blocked = noindex || isPrivatePath(p) || isNoindexFollow(p) || offProduction;
  // Compliance and confirmation pages stay crawlable but are noindex, nofollow; only off-production copies keep follow so link equity is not lost.
  const follow = !isPrivatePath(p) && !isNoindexFollow(p) && offProduction && !noindex;
  const full = fitTitle(title, SITE_NAME);
  const description = shortenDesc(String(rawDescription || "").replace(/(?<![\w/@.-])vistolane(?![\w.-])/g, "Vistolane"));
  const url = canonical || `${SITE_URL}${p}`;
  // Tells the boot script in index.html that the app owns the head from now on.
  document.documentElement.setAttribute("data-seo", "live");
  document.title = full;
  meta("name", "description", description);
  meta("name", "robots", blocked ? (follow ? "noindex, follow" : "noindex, nofollow") : "index, follow, max-image-preview:large, max-snippet:-1");
  // Dynamic renderers (Prerender-style) turn this into the real HTTP status for crawlers: 404 for unknown URLs.
  const sc = document.head.querySelector('meta[name="prerender-status-code"]');
  if (status) meta("name", "prerender-status-code", String(status)); else if (sc) sc.remove();
  document.head.querySelector('meta[name="prerender-header"]')?.remove();
  meta("property", "og:site_name", SITE_NAME);
  meta("property", "og:type", type);
  meta("property", "og:title", full);
  meta("property", "og:description", description);
  meta("property", "og:url", url);
  meta("property", "og:image", image ? optimized(image.startsWith("http") ? image : `${SITE_URL}${image}`, SITE_URL) : DEFAULT_IMAGE);
  meta("property", "og:image:width", "1200");
  meta("property", "og:image:height", "630");
  meta("name", "twitter:card", "summary_large_image");
  meta("name", "twitter:title", full);
  meta("name", "twitter:description", description);
  meta("name", "twitter:image", image || DEFAULT_IMAGE);
  link("canonical", url);
  let script = document.getElementById("seo-jsonld");
  if (!script) { script = document.createElement("script"); script.type = "application/ld+json"; script.id = "seo-jsonld"; document.head.appendChild(script); }
  const own = (jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []).filter(Boolean);
  const pageFaq = PAGE_FAQS[(path || window.location.pathname).replace(/\/+$/, "") || "/"];
  if (pageFaq && !own.some((n) => n?.["@type"] === "FAQPage")) own.push(faqJsonLd(pageFaq));
  const hasCrumbs = own.some((n) => n?.["@type"] === "BreadcrumbList");
  const graph = [...(hasCrumbs ? [] : [{ "@type": "BreadcrumbList", itemListElement: crumbs(path || window.location.pathname) }]), ...own, ...(schemaType && !own.length ? [{ "@type": schemaType, name: full, description, url }] : [])];
  const cleaned = normaliseGraph(graph, { site: SITE_URL, name: SITE_NAME, url, image, description });
  script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": cleaned });
}

const crumbs = (path) => crumbsLd(crumbsFor(path, destinationNames()), SITE_URL, path).itemListElement;

let claimed = "";
export function Seo(props) {
  const { pathname } = useLocation();
  useEffect(() => { noteNavigation(pathname); claimed = pathname; applySeo({ ...props, path: props.path || pathname }); }, [props.title, props.description, props.image, props.noindex, props.status, pathname, JSON.stringify(props.alternates || null), JSON.stringify(props.jsonLd || null)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    noteNavigation(pathname);
    if (claimed === pathname) return;
    const s = seoFor(pathname);
    if (s) applySeo({ ...s, jsonLd: pathname === "/" ? [faqJsonLd(HOME_FAQS.map((f) => [f.q, f.a]))] : s.jsonLd, path: pathname });
  }, [pathname]);
  return null;
}

export const faqJsonLd = (items) => ({
  "@type": "FAQPage",
  mainEntity: items.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});
